import { useCallback, useContext, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { Program } from "@coral-xyz/anchor";
import { AppConfigContext } from "../context";
import * as dripIdl from "../idl/drip_vault_v2.json";
import { RawDripPosition } from "../features/drip/aggregateDripDepositors";
import { DripVaultDescriptor } from "../constants/dripVaults";
import {
  DRIP_SCALE,
  dripProgramFor,
  fetchDripVaultState,
} from "../features/drip/dripFetch";
import { resolveSolPerShare } from "../features/drip/dripSolPerShare";
import { useDripVaults } from "./useDripVaults";

/**
 * Every position account for one vault, whichever family it belongs to.
 *
 * `AssetUserPosition` is one account type shared by every asset vault on the
 * program — `.all()` filters only by discriminator, not by vault, so it
 * would return positions from any asset vault ever initialised on this
 * program id, not just the one this loop iteration is pricing. Scope it to
 * this vault's deposit mint client-side. `UserPosition` (legacy kySOL) has
 * no such field and there is only ever one kySOL vault, so it needs no
 * filter.
 *
 * Deliberately does not swallow rejections: a failed RPC call should
 * propagate to the caller's try/catch so the dashboard can report the error
 * instead of silently rendering a partial or empty leaderboard.
 */
const allPositionAccounts = async (
  program: Program,
  vault: DripVaultDescriptor
): Promise<any[]> => {
  if (vault.kind === "kysol") {
    // @ts-ignore - account names resolve at runtime from the IDL
    return program.account.userPosition.all();
  }

  // @ts-ignore
  const accounts = await program.account.assetUserPosition.all();
  return accounts.filter(
    ({ account }: { account: any }) =>
      account.depositMint?.toBase58() === vault.depositMint
  );
};

/**
 * Fetches every Drip position account across every enabled vault, so the
 * dashboard can show who is dripping and into which reward token.
 *
 * Legacy kySOL positions are `UserPosition`; asset-vault positions are
 * `AssetUserPosition` — a different discriminator, so a single .all() call
 * silently misses one of them.
 *
 * Each position is tagged with its vault's SOL value per share so the
 * aggregator can rank positions held in different LSTs against each other.
 * Per-share values are resolved for every vault in one pass (see
 * dripSolPerShare) rather than independently per vault, because the
 * fallback-to-1 behaviour must apply uniformly across vaults to stay safe.
 */
export const useDripDepositors = () => {
  const { connection } = useConnection();
  const { dripProgramAddress } = useContext(AppConfigContext);
  const vaults = useDripVaults();

  const [positions, setPositions] = useState<RawDripPosition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDepositors = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Pin the program id to the env-configured address so `.all()` (which
      // filters by programId) queries the same program the rest of the app
      // uses, regardless of the address baked into the IDL.
      const programId = new PublicKey(dripProgramAddress);
      const program = dripProgramFor(dripIdl, dripProgramAddress, connection);

      // Gather: every vault's accounts and vault state, in parallel.
      const perVault = await Promise.all(
        vaults.map(async (vault: DripVaultDescriptor) => {
          const [accounts, vaultState] = await Promise.all([
            allPositionAccounts(program, vault),
            // No price needed; only stakedSol/shareSupply are used below.
            fetchDripVaultState(program, connection, vault, programId, null),
          ]);
          return { vault, accounts, vaultState };
        })
      );

      // Resolve: one SOL-per-share value per vault, decided together so the
      // fallback-to-1 rule is all-or-nothing across vaults.
      const solPerShareByVault = resolveSolPerShare(
        perVault.map(({ vault, vaultState }) => ({
          vaultId: vault.id,
          stakedSol: vaultState?.stakedSol ?? null,
          shareSupply: vaultState?.shareSupply ?? null,
        }))
      );

      // Map: build the flat position list using the resolved per-share values.
      const nextPositions = perVault.flatMap(({ vault, accounts }, index) =>
        accounts.map(({ account }: { account: any }) => ({
          owner: account.owner.toBase58(),
          shares:
            (Number(account.shares) + Number(account.pendingShares ?? 0)) /
            DRIP_SCALE,
          rewardTokenMint: account.rewardTokenMint.toBase58(),
          lastDepositEpoch: Number(account.lastDepositEpoch),
          vaultId: vault.id,
          solPerShare: solPerShareByVault[index],
        }))
      );

      setPositions(nextPositions);
    } catch (err) {
      console.error("Error fetching drip depositors:", err);
      setError("Error fetching Drip depositors.");
    } finally {
      setIsLoading(false);
    }
  }, [connection, dripProgramAddress, vaults]);

  return { positions, isLoading, error, fetchDepositors };
};
